import {
  REVISION_STAGE,
  STATUS_LABEL,
  pipelineStagesFor,
  stageIndexForStatus,
} from '../../lib/pipeline';
import type { GenerationRequestStatus } from '../../types/api';
import type { StreamConnection } from './useGenerationProgress';

interface StageTrackerProps {
  status: GenerationRequestStatus;
  /** Server note for the current stage, when there is one. */
  detail: string | null;
  connection: StreamConnection;
  /**
   * Decides the track: a run that renders something walks three stages a
   * copy-only run never does. See `hasVisualPhase`.
   */
  hasVisuals: boolean;
}

/** What the connection is doing, in the product's voice. Null when it is fine. */
function connectionNote(connection: StreamConnection): string | null {
  switch (connection) {
    case 'reconnecting':
      return 'Reconnecting. Your post keeps going either way.';
    case 'lost':
      return 'Connection lost — reload to resume. We are still working on your post; nothing is lost.';
    case 'idle':
    case 'connecting':
    case 'open':
      return null;
  }
}

/**
 * Live pipeline stepper for an in-flight request.
 *
 * The stages are the real agent stages (SRS §3), not a decorative sequence, and
 * the state shown is whatever the SSE stream last reported. Work continues in
 * the Arq worker regardless of this connection, so a drop is reported as a
 * connection problem, never as a failed run.
 *
 * A run that renders something — a carousel, or a single image we were asked to
 * draw — shows the visual phase (designing, visual checks, final review) from
 * the start, so the second half of the job is never a surprise. It is the same
 * tracker on the same stream after copy selection hands off to the design agent;
 * only the active stage moves on.
 *
 * Revising is the one stage that comes and goes. It is a detour off a review
 * step rather than a step of either track, so it is appended only while the run
 * reports `revising` and disappears again the moment it moves on — a run nobody
 * revised never carries a column for it.
 */
export function StageTracker({ status, detail, connection, hasVisuals }: StageTrackerProps) {
  const track = pipelineStagesFor(hasVisuals);
  const isRevising = status === 'revising';
  const stages = isRevising ? [...track, REVISION_STAGE] : track;
  const activeIndex = isRevising ? stages.length - 1 : stageIndexForStatus(status, hasVisuals);
  const note = connectionNote(connection);

  return (
    <section className="stage-card" aria-label="Progress">
      <ol className="stage-track">
        {stages.map((stage, index) => {
          const state = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending';
          return (
            <li className={`stage stage--${state}`} key={stage.key}>
              <span className="stage__mark" aria-hidden="true">
                {state === 'done' ? '✓' : '·'}
              </span>
              <span className="stage__label">{stage.label}</span>
            </li>
          );
        })}
      </ol>

      <p className="stage-card__readout" role="status" aria-live="polite">
        <span className="stage-card__status">{STATUS_LABEL[status]}</span>
        {detail ? <span className="stage-card__detail">{detail}</span> : null}
      </p>

      {note ? <p className="form__notice">{note}</p> : null}

      <p className="stage-card__note">
        You can leave this page. We&apos;ll carry on, and it&apos;ll be here when you come back.
      </p>
    </section>
  );
}
